import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { parseLocalized, localize, LANGS, SITE_URL } from '../lib/routes';

const seoData = {
  it: {
    home: {
      title: 'Studio Notarile Accordini & Carillo | Notai Associati Verona',
      description: 'Studio Notarile Associato con sedi a Verona, Valeggio sul Mincio e Peschiera del Garda. Compravendite, successioni, società, consulenza notarile professionale.',
    },
    services: {
      title: 'Servizi Notarili | Studio Accordini & Carillo',
      description: 'Tutti i servizi notarili: compravendite immobiliari, successioni, testamenti, costituzione società, donazioni, procure, autentiche.',
    },
    about: {
      title: 'Chi Siamo | Studio Notarile Accordini & Carillo',
      description: 'Tradizione e innovazione dal 1979. Conoscete il nostro team di notai e collaboratori.',
    },
    contact: {
      title: 'Contatti | Studio Notarile Accordini & Carillo',
      description: 'Contatta lo Studio Notarile Accordini & Carillo. Sedi a Verona, Valeggio sul Mincio e Peschiera del Garda.',
    },
    faq: {
      title: 'FAQ - Domande Frequenti | Studio Notarile Accordini & Carillo',
      description: 'Risposte alle domande più frequenti sui servizi notarili.',
    },
    privacy: {
      title: 'Privacy Policy | Studio Notarile Accordini & Carillo',
      description: 'Informativa sulla privacy e trattamento dei dati personali.',
    },
    cookie: {
      title: 'Cookie Policy | Studio Notarile Accordini & Carillo',
      description: 'Informativa sui cookie utilizzati dal sito.',
    }
  },
  en: {
    home: {
      title: 'Notary Office Accordini & Carillo | Notaries in Verona, Italy',
      description: 'Associated Notary Office with locations in Verona, Valeggio sul Mincio and Peschiera del Garda.',
    },
    services: {
      title: 'Notary Services | Studio Accordini & Carillo',
      description: 'Complete notary services: real estate transactions, inheritance, wills, company formation.',
    },
    about: {
      title: 'About Us | Notary Office Accordini & Carillo',
      description: 'Tradition and innovation since 1979. Meet our team of notaries.',
    },
    contact: {
      title: 'Contact Us | Notary Office Accordini & Carillo',
      description: 'Contact Notary Office Accordini & Carillo. Request an appointment.',
    },
    faq: {
      title: 'FAQ | Notary Accordini & Carillo',
      description: 'Answers to frequently asked questions about notary services.',
    },
    privacy: {
      title: 'Privacy Policy | Notary Office Accordini & Carillo',
      description: 'Privacy policy and personal data processing information.',
    },
    cookie: {
      title: 'Cookie Policy | Notary Office Accordini & Carillo',
      description: 'Cookie policy for the website.',
    }
  },
  de: {
    home: {
      title: 'Notariat Accordini & Carillo | Notare in Verona, Italien',
      description: 'Assoziiertes Notariat mit Standorten in Verona, Valeggio sul Mincio und Peschiera del Garda.',
    },
    services: {
      title: 'Notarielle Dienstleistungen | Studio Accordini & Carillo',
      description: 'Vollständige Notardienstleistungen: Immobilientransaktionen, Erbschaft, Testamente.',
    },
    about: {
      title: 'Über Uns | Notariat Accordini & Carillo',
      description: 'Tradition und Innovation seit 1979.',
    },
    contact: {
      title: 'Kontakt | Notariat Accordini & Carillo',
      description: 'Kontaktieren Sie das Notariat Accordini & Carillo.',
    },
    faq: {
      title: 'FAQ | Notariat Accordini & Carillo',
      description: 'Antworten auf häufig gestellte Fragen zu Notardienstleistungen.',
    },
    privacy: {
      title: 'Datenschutz | Notariat Accordini & Carillo',
      description: 'Datenschutzerklärung.',
    },
    cookie: {
      title: 'Cookie-Richtlinie | Notariat Accordini & Carillo',
      description: 'Cookie-Richtlinie der Website.',
    }
  },
  fr: {
    home: {
      title: 'Étude Notariale Accordini & Carillo | Notaires à Vérone',
      description: 'Étude Notariale Associée avec bureaux à Vérone, Valeggio sul Mincio et Peschiera del Garda.',
    },
    services: {
      title: 'Services Notariaux | Studio Accordini & Carillo',
      description: 'Services notariaux complets: transactions immobilières, succession, testaments.',
    },
    about: {
      title: 'Qui Sommes-Nous | Étude Notariale Accordini & Carillo',
      description: 'Tradition et innovation depuis 1979.',
    },
    contact: {
      title: 'Contact | Étude Notariale Accordini & Carillo',
      description: "Contactez l'Étude Notariale Accordini & Carillo.",
    },
    faq: {
      title: 'FAQ | Étude Notariale Accordini & Carillo',
      description: 'Réponses aux questions fréquemment posées.',
    },
    privacy: {
      title: 'Politique de Confidentialité | Accordini & Carillo',
      description: 'Politique de confidentialité.',
    },
    cookie: {
      title: 'Politique des Cookies | Accordini & Carillo',
      description: "Politique des cookies du site.",
    }
  }
};

const DEFAULT_TITLE = 'Studio Notarile Accordini & Carillo';
const DEFAULT_DESC = 'Studio Notarile Associato con sedi a Verona, Valeggio sul Mincio e Peschiera del Garda';

export const SEO = ({ page = 'home' }) => {
  const { language } = useLanguage();
  const { pathname } = useLocation();
  
  useEffect(() => {
    const langCode = language || 'it';
    const langData = seoData[langCode] || seoData.it;
    const pageData = langData[page] || langData.home;
    
    const title = pageData?.title || DEFAULT_TITLE;
    const description = pageData?.description || DEFAULT_DESC;
    
    // Update document title
    document.title = title;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description);
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', description);
    }
    
    // Update html lang
    document.documentElement.lang = langCode;

    // Canonical + hreflang alternates for the current localized page
    document.querySelectorAll('link[data-seo]').forEach((el) => el.remove());
    const parsed = parseLocalized(window.location.pathname);
    if (parsed) {
      const addLink = (rel, href, hreflang) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = SITE_URL + href;
        if (hreflang) el.hreflang = hreflang;
        el.setAttribute('data-seo', '1');
        document.head.appendChild(el);
      };
      addLink('canonical', localize(parsed.itPath, parsed.lang));
      LANGS.forEach((l) => addLink('alternate', localize(parsed.itPath, l), l));
      addLink('alternate', localize(parsed.itPath, 'it'), 'x-default');
    }
  }, [language, page, pathname]);

  return null; // This component doesn't render anything
};

export default SEO;
